import { motion, type HTMLMotionProps } from 'framer-motion'
import { feedback } from './feedback'

/**
 * Tap-feedback wrapper — scales to 0.97 while pressed, the app-wide
 * press convention. Renders a motion.button by default (correct
 * semantics + keyboard focus for free); pass `as="div"` for wrapping
 * non-interactive layouts where a nested button would be invalid.
 *
 * Also fires a tactile tick+haptic on activation (the app-wide "clicky"
 * feel). Override with `feel` — `'select'` for nav/segment changes,
 * `'dim'` for something receding, `'deny'` for a refusal, or `false` to
 * suppress (e.g. destructive or noisy repeated actions that play their own
 * richer feedback).
 *
 * All framer-motion + DOM props pass through (onClick, className,
 * disabled, aria-*, …).
 *
 * ── SHARED, NOT MOBILE-ONLY (moved from web/src/mobile/components/) ───────
 * Both shells import this now. `web/src/mobile/components/PressScale.tsx` is a
 * re-export shim so the ~38 mobile call sites are untouched.
 *
 * `lift` is the desktop half of the same idea: touch gets press-scale, a
 * pointer gets a 1px rise on hover. Opt-in so no existing mobile call site
 * changes behaviour. Both are transform-only and framer's
 * `MotionConfig reducedMotion="user"` (set at both shell roots) already
 * neutralises them for reduced-motion users.
 */
export function PressScale({
  as = 'button',
  children,
  feel = 'tap',
  lift = false,
  onClick,
  ...rest
}: {
  as?: 'button' | 'div'
  children: React.ReactNode
  /** Which feedback to fire on activation. Default 'tap'; false = silent. */
  feel?: 'tap' | 'select' | 'dim' | 'deny' | false
  /** Pointer-device hover rise (desktop). Off by default. */
  lift?: boolean
} & HTMLMotionProps<'button'>) {
  const shared = {
    whileTap: { scale: 0.97 },
    ...(lift ? { whileHover: { y: -1 } } : {}),
    transition: { duration: 0.15, ease: [0.22, 1, 0.36, 1] as const },
  }
  const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    // The negative verbs paint themselves onto the element that refused or
    // receded — that visual is how they read at all on a desktop with no
    // vibration hardware. `currentTarget` is that element by definition, so
    // this needs no ref and can never fight a ref a caller passed through.
    if (feel === 'dim' || feel === 'deny') feedback[feel](e.currentTarget)
    else if (feel) feedback[feel]()
    onClick?.(e as never)
  }
  if (as === 'div') {
    return (
      <motion.div {...shared} onClick={handleClick} {...(rest as HTMLMotionProps<'div'>)}>
        {children}
      </motion.div>
    )
  }
  return (
    <motion.button type="button" {...shared} onClick={handleClick} {...rest}>
      {children}
    </motion.button>
  )
}
